import { AlertCircleIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Button, Display, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { Badges } from '@prophecy/ui-v3/Badges';
import { pluralize } from '@prophecy/utils/string';
import styled from 'styled-components';

import { StatCard } from './styled';
import { SeatsSummary } from './types';
import { formatCredits, formatNumber } from './utils';

function SeatStat({
  label,
  value,
  badge,
  action
}: {
  label: string;
  value: number;
  badge?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <StatCard>
      <Stack gap={theme.spaces.x8}>
        <Text level='sm' tone={theme.textColor.tertiary}>
          {label}
        </Text>
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
          <Display level='xs' weight={theme.fontWeight.semiBold}>
            {formatNumber(value, 0)}
          </Display>
          <StackItem grow='1' />
          {badge}
          {action}
        </Stack>
      </Stack>
    </StatCard>
  );
}

const StyledSeatSectionContainer = styled(Stack)`
  margin-bottom: ${theme.spaces.x16};
`;

export function SeatsSection({ seats, onAddUsers }: { seats: SeatsSummary; onAddUsers: () => void }) {
  const overage = seats.activatedSeats - seats.totalSeats;

  return (
    <StyledSeatSectionContainer gap={theme.spaces.x16} data-test-id='sap-usage-seats'>
      <Stack>
        <Text level='md' weight={theme.fontWeight.medium}>
          {'Users '}
          <Text level='md' tone={theme.textColor.tertiary} elementType='span'>
            {'(Seats in your contract)'}
          </Text>
        </Text>
      </Stack>

      <Stack direction='horizontal' gap={theme.spaces.x16}>
        <SeatStat label='Total Seats' value={seats.totalSeats} />
        <SeatStat
          label='Activated Seats'
          value={seats.activatedSeats}
          badge={
            overage > 0 ? (
              <Badges size='s' variant='pillOutline' tone={BaseColor.error}>
                {overage} over total
              </Badges>
            ) : undefined
          }
        />
        <SeatStat label='Included Users' value={seats.includedUsers} />
        <SeatStat
          label='Additional Users'
          value={seats.additionalUsers}
          action={
            <Button size='s' variant='link' onClick={onAddUsers} data-test-id='sap-usage-add-users-button'>
              Add users
            </Button>
          }
        />
      </Stack>

      {overage > 0 && (
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          <AlertCircleIcon type='default' size='s' color={theme.colors.error600} />
          <Text level='sm' tone={theme.colors.error600}>
            {overage} {pluralize(overage, 'user')} over your seat limit
            {seats.creditsPerAdditionalUser === undefined
              ? '.'
              : ` — extra seats bill at ${formatCredits(seats.creditsPerAdditionalUser)} CU each.`}
          </Text>
        </Stack>
      )}
    </StyledSeatSectionContainer>
  );
}
